import { createSelector } from '@ngrx/store';
import { LoadingState, selectSearchResponse } from '@usf/ngrx-product';
import { productWithAlternativesSelector } from '../../shared/selectors/product.selectors';
import { orderItemSelectors } from '@usf/ngrx-order';
import {
  SearchResultViewModel,
  SearchViewModel,
} from '../shared/models/search-view.model';
import { selectedCustomer } from 'src/app/ngrx-customer/store';
import { ProductDetailViewModel } from '@product-detail/models/product-detail-view-model';
import { ProductFilter } from '@shared/helpers/product-filter.helpers';
import { ProductFilteringEnum } from '@shared/constants/product-filtering.enum';
import { selectAllFeaturesEnabled } from '@panamax/app-state';
import { FEATURES } from '@shared/constants/splitio-features';
import { isProprietaryProduct } from '@product-detail/utils/product-summary.utils';

export const searchResultsSelector = (previousPagesProductNumbers: number[]) =>
  createSelector(
    selectSearchResponse,
    productWithAlternativesSelector,
    selectedCustomer,
    (searchResponse, productsMap, selectedCustomer) => {
      let items: SearchResultViewModel[] = [];

      const allProductNumbers = [
        ...(previousPagesProductNumbers ?? []),
        ...(searchResponse.productNumbers ?? []),
      ];
      const allProductNumbersSet = [...new Set(allProductNumbers)];

      if (
        !!searchResponse.productNumbers &&
        searchResponse.productNumbers.every(
          productNumber => productsMap.get(productNumber) !== undefined,
        )
      ) {
        let index = 1;
        items = allProductNumbersSet.reduce((productList, productNumber) => {
          const product = productsMap.get(productNumber);
          if (product.summary.properties) {
            product.summary.properties = new Set([
              ...product.summary.properties,
              ...product.summary.additionalProperties,
            ]);
          }
          productList.push({
            ...product,
            selectedCustomer,
            resultRank: index++,
          });
          return productList;
        }, []);
      }
      return items;
    },
  );

export const searchViewModelSelector = createSelector(
  selectSearchResponse,
  searchResponse => {
    const searchViewModel: SearchViewModel = {
      searchRequest: null,
      searchResponse: null,
      error: false,
      listProducts: [],
    };

    // on error
    if (searchResponse?.loadingState === LoadingState.error) {
      searchViewModel.error = true;
      return searchViewModel;
    }
    // on load
    if (searchResponse?.loadingState === LoadingState.loaded) {
      searchViewModel.searchResponse = searchResponse;
      return searchViewModel;
    }

    // others
    return undefined;
  },
);

export const searchResultOrderItem = (productNumber: number) =>
  createSelector(
    orderItemSelectors.selectOrderItemState,
    orderItemState => orderItemState.entities[productNumber],
  );

export const topSellersSelector = createSelector(
  selectSearchResponse,
  productWithAlternativesSelector,
  selectedCustomer,
  selectAllFeaturesEnabled([FEATURES.split_dynamic_proprietary_ui.name]),
  (searchResponse, productsMap, selectedCustomer, isProprietaryUIEnabled) => {
    let items: ProductDetailViewModel[] = [];
    if (
      !!searchResponse?.topSellerProductNumbers &&
      searchResponse?.topSellerProductNumbers.every(
        productNumber => productsMap.get(productNumber) !== undefined,
      )
    ) {
      let index = 1;
      items = searchResponse.topSellerProductNumbers
        .filter(productNumber => {
          const product = productsMap.get(productNumber);
          if (isProprietaryProduct(product, isProprietaryUIEnabled)) {
            return false;
          }
          // applyFilter will return true if the product should be included
          return ProductFilter.applyFilter(
            product,
            selectedCustomer,
            ProductFilteringEnum.TOP_SELLERS,
          );
        })
        .reduce((productList, productNumber) => {
          const product = productsMap.get(productNumber);
          if (product.summary.properties) {
            product.summary.properties = new Set([
              ...product.summary.properties,
              ...product.summary.additionalProperties,
            ]);
          }
          let item: SearchResultViewModel = {
            ...product,
            selectedCustomer,
            resultRank: index++,
          };
          productList.push({
            ...item,
            selectedCustomer: item.selectedCustomer,
          });
          return productList;
        }, []);
    }
    return items;
  },
);
